/**
 * Canonical Checkpoint application identifiers.
 * Must stay in sync with auth/app/Services/Checkpoint/CheckpointAppName.php
 */
export const CHECKPOINT_APPS = {
  MEMBERS: 'MEMBERS',
  COORDINATOR: 'COORDINATOR',
  NOTES_EDITOR: 'NOTES_EDITOR',
  PDF_EDITOR: 'PDF_EDITOR',
  FLOW_EDITOR: 'FLOW_EDITOR',
  GRAPH_EDITOR: 'GRAPH_EDITOR',
} as const;

export type CheckpointAppName = typeof CHECKPOINT_APPS[keyof typeof CHECKPOINT_APPS];

/** Default app context when none is configured (members dashboard). */
export const DEFAULT_CHECKPOINT_APP = CHECKPOINT_APPS.MEMBERS;

/** Human-readable labels for UI display. */
export const CHECKPOINT_APP_LABELS: Record<CheckpointAppName, string> = {
  [CHECKPOINT_APPS.MEMBERS]: 'Members',
  [CHECKPOINT_APPS.COORDINATOR]: 'Coordinator PM',
  [CHECKPOINT_APPS.NOTES_EDITOR]: 'Notes',
  [CHECKPOINT_APPS.PDF_EDITOR]: 'PDF Editor',
  [CHECKPOINT_APPS.FLOW_EDITOR]: 'Design Flow',
  [CHECKPOINT_APPS.GRAPH_EDITOR]: 'Dream Graph',
};

/**
 * Apps assignable in the members create-user UI.
 * Add new apps here only — uiId must match `app-checkbox-{uiId}` in the form template.
 */
export const UI_APP_ID_TO_CHECKPOINT = {
  coordinator: CHECKPOINT_APPS.COORDINATOR,
  notes: CHECKPOINT_APPS.NOTES_EDITOR,
  dream: CHECKPOINT_APPS.GRAPH_EDITOR,
  design: CHECKPOINT_APPS.FLOW_EDITOR,
  pdf: CHECKPOINT_APPS.PDF_EDITOR,
} as const satisfies Record<string, CheckpointAppName>;

export type CheckpointUiAppId = keyof typeof UI_APP_ID_TO_CHECKPOINT;

/** UI form checkbox ids derived from {@link UI_APP_ID_TO_CHECKPOINT}. */
export const UI_APP_IDS = Object.keys(UI_APP_ID_TO_CHECKPOINT) as CheckpointUiAppId[];

export function isCheckpointAppName(value: string): value is CheckpointAppName {
  return (Object.values(CHECKPOINT_APPS) as string[]).includes(value);
}
